import { useTranslation } from "react-i18next";

import type { GtmSequenceMessageMode } from "../api/GtmApi";
import { useGtmSequenceNodeActivityQuery } from "../hooks/useGtmPlays";
import SequenceMessageStepEditor from "./SequenceMessageStepEditor";
import {
  isSequenceMessageStep,
  type SequenceDefinitionNode,
  type SequenceMessageStep,
} from "./sequenceToFlow";

type Props = {
  projectId: string;
  playId: string;
  nodeId: string;
  stepKey: string;
  label: string;
  subtitle?: string;
  kind: string;
  definitionNode?: SequenceDefinitionNode | null;
  messageMode?: GtmSequenceMessageMode;
  messageBody?: string;
  savingMessage?: boolean;
  messageError?: string | null;
  onClose: () => void;
  onSaveMessage?: (next: { messageMode: GtmSequenceMessageMode; messageBody: string }) => void;
  onOpenLead?: (leadId: string) => void;
};

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return "";
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso);
  if (!match) return iso;
  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Number(match[4]),
    Number(match[5])
  );
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function kindBadge(kind: string, t: (s: string) => string): string {
  switch (kind) {
    case "gtmWait":
      return t("Wait");
    case "gtmBranch":
      return t("Branch");
    case "gtmTerminal":
      return t("End");
    default:
      return t("Step");
  }
}

function eventKindLabel(kind: string, t: (s: string) => string): string {
  switch (kind) {
    case "performed":
      return t("Performed");
    case "draft":
      return t("Draft");
    case "reply":
      return t("Reply");
    case "classified":
      return t("Classified");
    case "notified":
      return t("Notified");
    case "ended":
      return t("Ended");
    case "error":
      return t("Error");
    default:
      return kind;
  }
}

export default function SequenceNodeDetailPanel({
  projectId,
  playId,
  nodeId,
  stepKey,
  label,
  subtitle,
  kind,
  definitionNode,
  messageMode = "ai",
  messageBody = "",
  savingMessage,
  messageError,
  onClose,
  onSaveMessage,
  onOpenLead,
}: Props) {
  const { t } = useTranslation();
  const activityQuery = useGtmSequenceNodeActivityQuery(projectId, playId, stepKey);
  const activity = activityQuery.data;
  const messageStep = isSequenceMessageStep(stepKey) ? (stepKey as SequenceMessageStep) : null;

  const detailLines: string[] = [];
  if (definitionNode?.delay) detailLines.push(`${t("Delay")}: ${definitionNode.delay}`);
  if (definitionNode?.gate) detailLines.push(`${t("Gate")}: ${definitionNode.gate}`);
  if (definitionNode?.branches?.on) detailLines.push(`${t("Branches on")}: ${definitionNode.branches.on}`);
  if (definitionNode?.outcome) detailLines.push(`${t("Outcome")}: ${definitionNode.outcome}`);
  if (definitionNode?.type === "wait") {
    detailLines.push(t("Wait nodes pause advancement; LinkedIn actions are logged on surrounding steps."));
  }

  return (
    <aside className="flex w-full max-w-md flex-col gap-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
            {kindBadge(kind, t)}
          </div>
          <h3 className="text-sm font-semibold text-gray-900">{label}</h3>
          {subtitle ? <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p> : null}
          <p className="mt-1 font-mono text-[11px] text-gray-400">{nodeId}</p>
        </div>
        <button
          type="button"
          className="text-sm text-gray-500 hover:text-gray-800"
          onClick={onClose}
          aria-label={t("Close")}
        >
          ×
        </button>
      </div>

      {detailLines.length > 0 ? (
        <ul className="space-y-1 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-700">
          {detailLines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      ) : null}

      {messageStep && onSaveMessage ? (
        <SequenceMessageStepEditor
          step={messageStep}
          messageMode={messageMode}
          messageBody={messageBody}
          saving={savingMessage}
          error={messageError}
          embedded
          onClose={onClose}
          onSave={onSaveMessage}
        />
      ) : null}

      <section className="space-y-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          {t("People here")}
          {activity ? ` (${activity.people.length})` : ""}
        </h4>
        {activityQuery.isLoading ? (
          <p className="text-xs text-gray-500">{t("Loading…")}</p>
        ) : activityQuery.isError ? (
          <p className="text-xs text-red-600">{t("Could not load people for this step")}</p>
        ) : !activity?.people.length ? (
          <p className="text-xs text-gray-500">{t("No leads currently on this node")}</p>
        ) : (
          <ul className="max-h-36 space-y-1 overflow-y-auto">
            {activity.people.map((person) => (
              <li key={person.leadId}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm hover:bg-slate-50"
                  onClick={() => onOpenLead?.(person.leadId)}
                >
                  <span className="truncate font-medium text-gray-900">{person.name}</span>
                  <span className="ml-2 shrink-0 text-[11px] text-gray-500">
                    {person.nextActionAt ? formatWhen(person.nextActionAt) : person.status}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          {t("Activity log")}
          {activity ? ` (${activity.events.length})` : ""}
        </h4>
        {activityQuery.isLoading ? (
          <p className="text-xs text-gray-500">{t("Loading…")}</p>
        ) : activityQuery.isError ? (
          <p className="text-xs text-red-600">{t("Could not load activity for this step")}</p>
        ) : !activity?.events.length ? (
          <p className="text-xs text-gray-500">{t("No events logged for this node yet")}</p>
        ) : (
          <ul className="max-h-64 space-y-2 overflow-y-auto">
            {activity.events.map((event) => (
              <li
                key={event.id}
                className="rounded-md border border-gray-100 bg-slate-50 px-2.5 py-2 text-xs"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-gray-900">{event.leadName}</span>
                  <span className="shrink-0 text-[10px] uppercase tracking-wide text-gray-500">
                    {eventKindLabel(event.kind, t)}
                  </span>
                </div>
                {event.body ? (
                  <p className="mt-1 whitespace-pre-wrap break-words text-gray-700">{event.body}</p>
                ) : null}
                <p className="mt-1 text-[10px] text-gray-400">{formatWhen(event.createdAt)}</p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </aside>
  );
}
